
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { dealCategories } from '../../../../mocks/deals';

interface Deal {
  id: number;
  businessName: string;
  businessLogo: string;
  category: string;
  dealTitle: string;
  description: string;
  originalPrice: number;
  dealPrice: number;
  discountPercent: number;
  image: string;
  couponCode: string;
  expiresAt: string;
  claimed: number;
  rating: number;
  reviews: number;
}

interface DealHeroProps {
  deal: Deal;
}

export default function DealHero({ deal }: DealHeroProps) {
  const [copiedCode, setCopiedCode] = useState(false);

  const categoryLabel =
    dealCategories.find((c) => c.id === deal.category)?.name || deal.category;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deal.couponCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2500);
    } catch {
      // Fallback for browsers that do not support the Clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = deal.couponCode;
      textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in MS Edge.
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2500);
      } catch (fallbackError) {
        console.error('Copy fallback failed', fallbackError);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  return (
    <section className="relative pt-20">
      {/* Back navigation */}
      <div className="absolute top-24 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <Link
            to="/deals"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-base"></i>
            </div>
            Back to Deals
          </Link>
        </div>
      </div>

      {/* Hero image */}
      <div className="relative h-[420px] overflow-hidden">
        <img
          src={deal.image}
          alt={`${deal.dealTitle} at ${deal.businessName} Roseville CA`}
          title={`${deal.businessName} deal coupon Roseville`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-gray-900/20"></div>

        {/* Overlay content */}
        <div className="absolute bottom-0 left-0 right-0 pb-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                <i className="ri-fire-fill text-xs"></i>
                {deal.discountPercent === 100 ? 'FREE' : `${deal.discountPercent}% OFF`}
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                {categoryLabel}
              </span>
              {deal.expiresAt !== 'Ongoing' && (
                <span className="px-3 py-1 bg-amber-500/90 text-white text-xs font-medium rounded-full flex items-center gap-1">
                  <i className="ri-time-line text-xs"></i>
                  Expires {deal.expiresAt}
                </span>
              )}
            </div>

            <h1 className="text-4xl font-bold text-white mb-2 leading-tight max-w-2xl">
              {deal.dealTitle}
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <i className={`${deal.businessLogo} text-white text-base`}></i>
              </div>
              <span className="text-white/90 text-sm font-medium">{deal.businessName}</span>
              <div className="flex items-center gap-1 ml-2">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <span className="text-white font-semibold text-sm">{deal.rating}</span>
                <span className="text-white/60 text-xs">({deal.reviews} reviews)</span>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-baseline gap-3">
                {deal.dealPrice > 0 ? (
                  <>
                    <span className="text-3xl font-bold text-white">${deal.dealPrice}</span>
                    <span className="text-lg text-white/50 line-through">
                      ${deal.originalPrice}
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-white">FREE</span>
                )}
                {deal.dealPrice > 0 && (
                  <span className="text-sm text-teal-300 font-semibold">
                    Save ${(deal.originalPrice - deal.dealPrice).toFixed(2)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm rounded-lg border border-dashed border-white/30">
                  <i className="ri-scissors-cut-line text-amber-400 text-sm"></i>
                  <span className="text-sm font-mono font-bold text-white tracking-wider">
                    {deal.couponCode}
                  </span>
                </div>
                <button
                  onClick={handleCopy}
                  className="whitespace-nowrap px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                >
                  {copiedCode ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-line"></i> Copied!
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <i className="ri-file-copy-line"></i> Copy Code
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
